import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SfAccountSnapshot } from '@/types';
import { api } from '@/lib/api';
import { Loader2, RotateCcw, AlertTriangle } from 'lucide-react';

interface UndoCancellationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: SfAccountSnapshot;
  onSuccess: () => void;
}

function formatDateForDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function UndoCancellationModal({
  open,
  onOpenChange,
  account,
  onSuccess,
}: UndoCancellationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await api.undoCancellation(account.sf_account_id);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onSuccess();
        }, 2000);
      } else {
        setError(result.error || 'Failed to undo cancellation');
      }
    } catch (err: unknown) {
      // Handle ApiError from the API client
      const apiError = err as { error?: string };
      const errorMessage = apiError?.error || (err instanceof Error ? err.message : 'An unexpected error occurred');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setSuccess(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-abby-primary" />
            Undo Cancellation
          </DialogTitle>
          <DialogDescription>
            Remove the scheduled cancellation and continue service as normal.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-gray-600">Cancellation undone successfully!</p>
            <p className="text-sm text-gray-500 mt-1">Service will continue as normal.</p>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {account.ai_contract_end && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Currently scheduled to end on{' '}
                    <strong>{formatDateForDisplay(account.ai_contract_end)}</strong>
                  </p>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Confirm undo cancellation</p>
                    <p className="text-amber-700 mt-1">
                      This will remove the scheduled end date and service will continue as normal with
                      regular billing.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Keep Cancellation
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Undo Cancellation'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
